import { useReducer, useCallback } from "react";
import { FIELD_REGISTRY, FieldId, FormValues, MEMBER_FIELDS, isMemberValueFilled } from "./fieldRegistry";

type FormAction =
  | { type: "SET_FIELD"; fieldId: FieldId; value: unknown }
  | { type: "RESET_FIELD"; fieldId: FieldId }
  | { type: "RESET_ALL" }
  | { type: "HYDRATE"; values: FormValues };

const COMPLETE_STATUS_OPTION = Object.freeze([Object.freeze({ id: "COMPLETE", label: "Complete" })]);

function getInitialValueForType(filterType: string): unknown {
  switch (filterType) {
    case "MultiSelect":
    case "SearchableMultiSelect":
    case "TextField":
      return [];
    case "DatePicker":
    case "SingleSelect":
      return null;
    default:
      return null;
  }
}

function formReducer(state: FormValues, action: FormAction): FormValues {
  switch (action.type) {
    case "SET_FIELD": {
      const next = { ...state, [action.fieldId]: action.value };
      FIELD_REGISTRY.forEach((spec) => {
        if (spec.resetWhen?.includes(action.fieldId)) {
          next[spec.id] = getInitialValueForType(spec.filterType);
        }
      });
      if (MEMBER_FIELDS.includes(action.fieldId as FieldId)) {
        const hasMember = MEMBER_FIELDS.some((f) => isMemberValueFilled(next[f]));
        next.caseStatus = hasMember ? COMPLETE_STATUS_OPTION : [];
      }
      return next;
    }
    case "RESET_FIELD": {
      const next = {
        ...state,
        [action.fieldId]: getInitialValueForType(
          FIELD_REGISTRY.find((f) => f.id === action.fieldId)?.filterType || "TextField"
        ),
      };
      FIELD_REGISTRY.forEach((spec) => {
        if (spec.resetWhen?.includes(action.fieldId)) {
          next[spec.id] = getInitialValueForType(spec.filterType);
        }
      });
      if (MEMBER_FIELDS.includes(action.fieldId as FieldId)) {
        const hasMember = MEMBER_FIELDS.some((f) => isMemberValueFilled(next[f]));
        next.caseStatus = hasMember ? COMPLETE_STATUS_OPTION : [];
      }
      return next;
    }
    case "RESET_ALL":
      return buildInitialState();
    case "HYDRATE": {
      const next = { ...buildInitialState(), ...action.values };
      const hasMember = MEMBER_FIELDS.some((f) => isMemberValueFilled(next[f]));
      if (hasMember) {
        next.caseStatus = COMPLETE_STATUS_OPTION;
      }
      return next;
    }
    default:
      return state;
  }
}

function buildInitialState(): FormValues {
  const state: FormValues = {};
  FIELD_REGISTRY.forEach((spec) => {
    state[spec.id] = getInitialValueForType(spec.filterType);
  });
  return state;
}

export function useSearchFormState() {
  const [formValues, dispatch] = useReducer(formReducer, undefined, buildInitialState);

  const setField = useCallback((fieldId: FieldId, value: unknown) => {
    dispatch({ type: "SET_FIELD", fieldId, value });
  }, []);

  const resetField = useCallback((fieldId: FieldId) => {
    dispatch({ type: "RESET_FIELD", fieldId });
  }, []);

  const resetAll = useCallback(() => {
    dispatch({ type: "RESET_ALL" });
  }, []);

  const hydrate = useCallback((values: FormValues) => {
    dispatch({ type: "HYDRATE", values });
  }, []);

  return { formValues, setField, resetField, resetAll, hydrate };
}
